/**
 * 학년도 클래스 보관 파일 내려받기 (zip: manifest.json + data/*.json + shared/*.json + files/<bucket>/<path>)
 * - 비밀번호(선택)를 넣으면 zip 전체를 AES-GCM(PBKDF2-SHA256)으로 암호화한 .cparchive 파일을 만든다.
 * - 조회는 로그인 사용자 권한(RLS)으로만 한다. 이용권(FREE 차단) 확인은 호출 화면에서 한다.
 */
import { zipSync, strToU8 } from 'fflate';
import { supabase } from '../supabaseClient';
import type { Grade } from '../types/domain';
import { academicYearEnd, academicYearGraceEnd } from '../utils/classAcademicYear';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';

export const CLASS_ARCHIVE_FORMAT_VERSION = 1;
const PAGE = 1000;
const ENC_MAGIC = 'CPARC1';
const PBKDF2_ITERATIONS = 250000;

/** 클래스(grade_id) 단위 표 */
const GRADE_SCOPED_TABLES = [
  'tracks',
  'base_timetable_cells',
  'daily_timetable_cells',
  'daily_timetable_overrides',
  'annual_timetable_cells',
  'annual_timetable_week_memos',
  'program_progress_cells',
  'program_fixed_content_rows',
  'program_scoped_content_rows',
  'annual_project_programs',
  'weekly_learning_guide_notes',
  'weekly_learning_guide_track_colors',
  'annual_behavior_notes',
  'annual_cross_topic_plans',
  'school_grade_representatives',
  'class_member_groups',
  'class_member_point_records',
  'class_member_coin_records',
  'class_member_notes',
  'class_member_item_instances',
  'class_member_assignment_rewards',
  'class_member_profile_cards',
  'member_profile_image_assets',
  'assignment_point_notes',
  'point_item_notes',
  'workspace_evaluation_plan_applied',
  'presentation_communication',
  'assignment_challenge_projects',
] as const;

/** 여러 학년도가 함께 쓰는 자료(사본으로만 포함, 서버 삭제 대상 아님) */
const SHARED_WORKSPACE_TABLES = [
  'program_color_rules',
  'workspace_learning_content_templates',
  'workspace_learning_content_rows',
  'workspace_profile_card_sets',
  'workspace_profile_card_set_settings',
  'workspace_evaluation_plan_level_settings',
  'workspace_slide_registered_pages',
  'workspace_slide_registered_layouts',
  'workspace_worksheet_registered_pages',
  'program_content_slide_pages',
  'program_content_worksheets',
  'budget_note_workspace_settings',
] as const;

type Row = Record<string, unknown>;
type Filter = (q: any) => any; // eslint-disable-line @typescript-eslint/no-explicit-any

export type ClassArchiveProgress = { step: string; done: number; total: number };

export type ClassArchiveResult = {
  blob: Blob;
  fileName: string;
  bytes: number;
  fileCount: number;
  warnings: string[];
};

async function fetchAll(table: string, filter: Filter, warnings: string[]): Promise<Row[]> {
  const out: Row[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await filter(supabase.from(table).select('*')).range(from, from + PAGE - 1);
    if (error) {
      warnings.push(`${table}: ${error.message}`);
      return out;
    }
    const rows = (data ?? []) as Row[];
    out.push(...rows);
    if (rows.length < PAGE) return out;
  }
}

function ymdIso(p: { y: number; m: number; d: number }): string {
  return `${p.y}-${String(p.m).padStart(2, '0')}-${String(p.d).padStart(2, '0')}`;
}

function safeName(s: string): string {
  return s.replace(/[\\/:*?"<>|\s]+/g, '_').slice(0, 40) || 'class';
}

function strCol(rows: Row[], col: string): string[] {
  return Array.from(new Set(rows.map((r) => r[col]).filter((v): v is string => typeof v === 'string' && !!v)));
}

async function encryptZip(zip: Uint8Array, password: string): Promise<Uint8Array> {
  const subtle = window.crypto.subtle;
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const baseKey = await subtle.importKey('raw', strToU8(password), 'PBKDF2', false, ['deriveKey']);
  const key = await subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
  const cipher = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv }, key, zip));
  const magic = strToU8(ENC_MAGIC);
  const out = new Uint8Array(magic.length + salt.length + iv.length + cipher.length);
  out.set(magic, 0);
  out.set(salt, magic.length);
  out.set(iv, magic.length + salt.length);
  out.set(cipher, magic.length + salt.length + iv.length);
  return out;
}

/** 암호화된 보관 파일을 zip 바이트로 되돌림(뷰어·검증용) */
export async function decryptClassArchive(data: Uint8Array, password: string): Promise<Uint8Array> {
  const magic = strToU8(ENC_MAGIC);
  for (let i = 0; i < magic.length; i += 1) {
    if (data[i] !== magic[i]) throw new Error('암호화된 보관 파일이 아닙니다.');
  }
  const salt = data.slice(magic.length, magic.length + 16);
  const iv = data.slice(magic.length + 16, magic.length + 28);
  const subtle = window.crypto.subtle;
  const baseKey = await subtle.importKey('raw', strToU8(password), 'PBKDF2', false, ['deriveKey']);
  const key = await subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
  return new Uint8Array(await subtle.decrypt({ name: 'AES-GCM', iv }, key, data.slice(magic.length + 28)));
}

export async function buildClassArchive(params: {
  workspaceId: string;
  userId: string;
  grade: Grade;
  password?: string;
  onProgress?: (p: ClassArchiveProgress) => void;
}): Promise<ClassArchiveResult> {
  const { workspaceId, userId, grade, password, onProgress } = params;
  const year = typeof grade.academic_year === 'number' ? grade.academic_year : null;
  const warnings: string[] = [];
  const files: Record<string, Uint8Array> = {};
  const counts: Record<string, number> = {};
  const put = (path: string, rows: unknown) => {
    files[path] = strToU8(JSON.stringify(rows, null, 1));
    if (Array.isArray(rows)) counts[path] = rows.length;
  };
  const totalSteps = GRADE_SCOPED_TABLES.length + SHARED_WORKSPACE_TABLES.length + 12;
  let step = 0;
  const tick = (label: string) => onProgress?.({ step: label, done: (step += 1), total: totalSteps });

  // 1) 클래스·명단
  put('data/grade.json', [grade]);
  {
    const { data, error } = await supabase.rpc('class_members_list', {
      p_workspace_id: workspaceId,
      p_grade_id: grade.id,
    });
    if (error) warnings.push(`class_members: ${error.message}`);
    put('data/class_members.json', (data as Row[] | null) ?? []);
    tick('학생 명단');
  }

  // 2) 클래스 단위 표
  const byTable: Record<string, Row[]> = {};
  for (const t of GRADE_SCOPED_TABLES) {
    byTable[t] = await fetchAll(t, (q) => q.eq('workspace_id', workspaceId).eq('grade_id', grade.id), warnings);
    put(`data/${t}.json`, byTable[t]);
    tick(t);
  }

  // 3) 하위 표(챌린지·소통)
  const projectIds = strCol(byTable.assignment_challenge_projects, 'id');
  for (const t of ['assignment_challenge_items', 'assignment_challenge_submissions', 'assignment_challenge_photos']) {
    byTable[t] = projectIds.length ? await fetchAll(t, (q) => q.in('project_id', projectIds), warnings) : [];
    put(`data/${t}.json`, byTable[t]);
    tick(t);
  }
  const refIds = strCol(byTable.presentation_communication, 'presentation_ref_id');
  for (const t of ['presentation_communication_questions', 'presentation_communication_responses']) {
    byTable[t] = refIds.length
      ? await fetchAll(t, (q) => q.eq('workspace_id', workspaceId).in('presentation_ref_id', refIds), warnings)
      : [];
    put(`data/${t}.json`, byTable[t]);
    tick(t);
  }

  // 4) 학년도 범위 자료(학사일정·일일기록·할일노트·예산노트)
  if (year != null) {
    const start = `${year}-03-01`;
    const end = ymdIso(academicYearEnd(year));
    put(
      'data/academic_year_settings.json',
      await fetchAll('academic_year_settings', (q) => q.eq('workspace_id', workspaceId).eq('academic_year', year), warnings)
    );
    put(
      'data/academic_terms.json',
      await fetchAll('academic_terms', (q) => q.eq('workspace_id', workspaceId).eq('academic_year', year), warnings)
    );
    put(
      'data/calendar_days.json',
      await fetchAll('calendar_days', (q) => q.eq('workspace_id', workspaceId).gte('day', start).lte('day', end), warnings)
    );
    tick('학사일정');
  } else {
    warnings.push('학년도 정보가 없어 학사일정을 넣지 않았습니다.');
  }

  // 5) 공유 자료 사본
  const shared: Record<string, Row[]> = {};
  for (const t of SHARED_WORKSPACE_TABLES) {
    shared[t] = await fetchAll(t, (q) => q.eq('workspace_id', workspaceId), warnings);
    put(`shared/${t}.json`, shared[t]);
    tick(t);
  }
  const setIds = strCol(shared.workspace_profile_card_sets, 'id');
  shared.workspace_profile_card_slots = setIds.length
    ? await fetchAll('workspace_profile_card_slots', (q) => q.in('set_id', setIds), warnings)
    : [];
  put('shared/workspace_profile_card_slots.json', shared.workspace_profile_card_slots);
  const sharedAssetTables: Array<[string, string]> = [
    ['slide_image_assets', 'slide-images'],
    ['learning_worksheet_image_assets', 'learning-worksheet-images'],
    ['evaluation_sheet_image_assets', 'evaluation-sheet-images'],
    ['item_card_image_assets', 'item-card-images'],
    ['assignment_cover_image_assets', 'assignment-cover-images'],
  ];
  const assetRows: Record<string, Row[]> = {};
  for (const [t] of sharedAssetTables) {
    assetRows[t] = await fetchAll(t, (q) => q.eq('user_id', userId), warnings);
    put(`shared/${t}.json`, assetRows[t]);
  }
  tick('공유 자료');

  // 6) 첨부 파일
  const targets: Array<{ bucket: string; path: string }> = [];
  strCol(byTable.member_profile_image_assets, 'storage_path').forEach((p) => targets.push({ bucket: 'member-profile-images', path: p }));
  strCol(byTable.assignment_challenge_photos, 'path').forEach((p) => targets.push({ bucket: 'assignment-challenge-photos', path: p }));
  strCol(shared.workspace_profile_card_slots, 'storage_path').forEach((p) => targets.push({ bucket: 'member-profile-images', path: p }));
  sharedAssetTables.forEach(([t, bucket]) =>
    strCol(assetRows[t], 'storage_path').forEach((p) => targets.push({ bucket, path: p }))
  );
  const seen = new Set<string>();
  let fileCount = 0;
  for (let i = 0; i < targets.length; i += 1) {
    const { bucket, path } = targets[i];
    const key = `files/${bucket}/${path.replace(/^\/+/, '')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const { data, error } = await supabase.storage.from(bucket).download(path);
    if (error || !data) {
      warnings.push(`파일 ${bucket}/${path}: ${error?.message ?? '없음'}`);
      continue;
    }
    files[key] = new Uint8Array(await data.arrayBuffer());
    fileCount += 1;
    onProgress?.({ step: `첨부 파일 ${i + 1}/${targets.length}`, done: step, total: totalSteps });
  }

  const title = parseGradeDisplayName(grade.name).title;
  const manifest = {
    format: 'classplanner-class-archive',
    version: CLASS_ARCHIVE_FORMAT_VERSION,
    exportedAt: new Date().toISOString(),
    academicYear: year,
    academicYearEnd: year != null ? ymdIso(academicYearEnd(year)) : null,
    serverDeleteAfter: year != null ? ymdIso(academicYearGraceEnd(year)) : null,
    className: title,
    gradeId: grade.id,
    counts,
    fileCount,
    warnings,
    note: '학생 개인정보가 들어 있습니다. 안전하게 보관하고 필요 없으면 파기해 주세요.',
  };
  files['manifest.json'] = strToU8(JSON.stringify(manifest, null, 1));
  tick('압축');

  let bytes: Uint8Array = zipSync(files, { level: 6 });
  const base = `${year != null ? `${year}학년도_` : ''}${safeName(title)}`;
  let fileName = `${base}.zip`;
  if (password) {
    bytes = await encryptZip(bytes, password);
    fileName = `${base}.cparchive`;
  }
  const blob = new Blob([bytes], { type: password ? 'application/octet-stream' : 'application/zip' });
  return { blob, fileName, bytes: bytes.length, fileCount, warnings };
}

/** 학년도 일반 자료 묶음(일일기록·할일노트·예산노트·공유 할일 보드) — bundle_id 기준 내려받기 */
export async function buildYearGeneralArchive(params: {
  userId: string;
  bundleId: string;
  year: number;
  password?: string;
  onProgress?: (p: ClassArchiveProgress) => void;
}): Promise<ClassArchiveResult> {
  const { userId, bundleId, year, password, onProgress } = params;
  const warnings: string[] = [];
  const files: Record<string, Uint8Array> = {};
  const counts: Record<string, number> = {};
  const put = (path: string, rows: unknown) => {
    files[path] = strToU8(JSON.stringify(rows, null, 1));
    if (Array.isArray(rows)) counts[path] = rows.length;
  };
  const total = 5;
  let step = 0;
  const tick = (label: string) => onProgress?.({ step: label, done: (step += 1), total });
  const end = ymdIso(academicYearEnd(year));
  const byBundle = (t: string) => fetchAll(t, (q) => q.eq('bundle_id', bundleId), warnings);

  put('data/daily_record_notes.json', await byBundle('daily_record_notes'));
  put('data/daily_note_categories.json', await fetchAll('daily_note_categories', (q) => q.eq('user_id', userId), warnings));
  tick('일일기록');
  put('data/todo_note_items.json', await byBundle('todo_note_items'));
  put('data/todo_note_categories.json', await fetchAll('todo_note_categories', (q) => q.eq('user_id', userId), warnings));
  tick('할일노트');
  const boards = await fetchAll(
    'todo_share_boards',
    (q) => q.eq('owner_user_id', userId).eq('bundle_id', bundleId),
    warnings
  );
  // 입장 코드·토큰은 파일에 넣지 않음
  put(
    'data/todo_share_boards.json',
    boards.map(({ public_token: _t, code_hash: _h, entry_code: _c, ...rest }) => rest)
  );
  const boardIds = strCol(boards, 'id');
  put(
    'data/todo_share_categories.json',
    boardIds.length ? await fetchAll('todo_share_categories', (q) => q.in('board_id', boardIds), warnings) : []
  );
  put(
    'data/todo_share_items.json',
    boardIds.length ? await fetchAll('todo_share_items', (q) => q.in('board_id', boardIds), warnings) : []
  );
  tick('공유 할일 보드');
  put('data/budget_note_rows.json', await byBundle('budget_note_rows'));
  put('data/budget_note_usage_entries.json', await byBundle('budget_note_usage_entries'));
  tick('예산노트');

  files['manifest.json'] = strToU8(
    JSON.stringify(
      {
        format: 'classplanner-year-general-archive',
        version: CLASS_ARCHIVE_FORMAT_VERSION,
        exportedAt: new Date().toISOString(),
        academicYear: year,
        bundleId,
        academicYearEnd: end,
        serverDeleteAfter: ymdIso(academicYearGraceEnd(year)),
        counts,
        warnings,
      },
      null,
      1
    )
  );
  tick('압축');
  let bytes: Uint8Array = zipSync(files, { level: 6 });
  const base = `${year}학년도_일반자료`;
  let fileName = `${base}.zip`;
  if (password) {
    bytes = await encryptZip(bytes, password);
    fileName = `${base}.cparchive`;
  }
  const blob = new Blob([bytes], { type: password ? 'application/octet-stream' : 'application/zip' });
  return { blob, fileName, bytes: bytes.length, fileCount: 0, warnings };
}

export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
